import warehouse from 'services/warehouse';

import { Product } from 'redux/models';

// import Filter from './Filter';  TODO EP-7208
import SearchBase from './SearchBase';

/**
 * Product Alternatives search implementation.
 */
export default class ProductCatalog extends SearchBase {
  static defaultProps = {
    // These are the current search results.
    searchResults: {
      count: 0, // total number of search results
      filters: [], // server side available filters
      keyword: '', // the server side search keyword
      products: [], // the search results
      sort: '', // how the search results were ordered
      query: '', // the client side search query
      queryParams: '', // additional client side search query parameters

      // pagination
      currentpage: 1,
      next: null, // url to next page of results or null
      prev: null, // url to next page of results or null
      totalpages: 1
    }
  };

  constructor (overrides) {
    super({ ...ProductCatalog.defaultProps, ...overrides });
  }

  /** Creates a new ProductCatalog search copy with optional overrides. */
  makeCopy (overrides) {
    const copy = new ProductCatalog({
      ...ProductCatalog.defaultProps,
      ...this.fields,
      ...overrides
    });

    return copy;
  }

  /**
   * Returns whether or not the search is async.
   */
  isAsync () {
    return true;
  }

  /**
   * Runs search query generating updated search results.
   */
  async runQuery ({ searchQuery = '', queryParams = '' } = {}) {
    const response = await warehouse.productCatalogSearchV2({ searchQuery, queryParams });

    const products = response.results.map(whProduct => {
      return Product.createProductFromWarehouseProductResponse({ whProduct });
    });

    const searchResults = {
      filters: response.filters.filters,
      keyword: response.keyword,
      products,
      count: response.count,
      query: searchQuery,
      queryParams,

      // pagination
      currentpage: response.currentpage,
      next: response.next,
      prev: response.prev,
      totalpages: response.totalpages
    };

    return searchResults;
  }

  /**
   * Pagination
   */
  hasPagination () { return true; }
  hasNextPage () { return this.fields.searchResults.next; }
  hasPreviousPage () { return this.fields.searchResults.prev; }
  loadNextPage () { return this.loadPage(this.fields.searchResults.next); }
  loadPreviousPage () { return this.loadPage(this.fields.searchResults.prev); }

  async loadPage (requestUrl) {
    const searchResults = this.fields.searchResults;
    if (!requestUrl) {
      return searchResults;
    }

    const response = await warehouse.productCatalogSearchV2({ requestUrl });

    const products = response.results.map(whProduct => {
      return Product.createProductFromWarehouseProductResponse({ whProduct });
    });

    return {
      ...searchResults,
      products,

      // pagination
      currentpage: response.currentpage,
      next: response.next,
      prev: response.prev,
      totalpages: response.totalpages
    };
  }

  /**
   * This search supports caching.
   */
  hasCaching () {
    return true;
  }

  /**
   * Returns cached search results if available.
   * @returns {Object} cache
   * @returns {boolean} cache.hit - True if search result is found in cache
   * @returns {searchResults|null} cache.searchResults - The search results if cache.hit is true; otherwise, null.
   */
  checkCache ({ searchQuery, queryParams }) {
    const searchResults = this.fields.searchResults;

    if (
      searchQuery === searchResults.query &&
      queryParams === searchResults.queryParams &&
      searchResults.count > 0
    ) {
      return {
        hit: true,
        searchResults
      };
    }
    return SearchBase.cacheMiss;
  }

  /**
   * Returns the last search execution query.
   */
  searchQuery () {
    return this.fields.searchResults.query;
  }

  /**
   * Returns the products matching the last search execution.
   * @returns {Product[]} products - Matching products
   */
  products () {
    return this.fields.searchResults.products;
  }

  /**
   * Returns the total number of matches for the last search execution.
   */
  count () {
    return this.fields.searchResults.count;
  }

  /**
   * Returns the total number of pages for the last search execution.
   */
  totalPages () {
    return this.fields.searchResults.totalpages;
  }

  /**
   * Returns the current pages for the last search execution.
   */
  currentPage () {
    return this.fields.searchResults.currentpage;
  }

  /**
   * Returns the dynamic filters available based
   * on the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Filter[]} filters
   */
  availableFilters (state) {
    return this.fields.searchResults.filters;
  }

  /**
   * Returns the number of results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOfResults ({ state }) {
    return this.fields.searchResults.count;
  }

  /**
   * Returns the number of 2d results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOf2dResults ({ state }) {
    return this.fields.searchResults.numberOf2dResults;
  }

  /**
   * Returns the number of 3d results from
   * the last search execution.
   * @param {Object} state - The redux state object.
   * @returns {Number} number of results
   */
  numberOf3dResults ({ state }) {
    return this.fields.searchResults.numberOf3dResults;
  }

  /**
   * Returns the current searchResults object.
   * @returns {Object} searchResults
   */
  searchResults () {
    return this.fields.searchResults;
  }
}
